import { gql } from '@eti/schema-types';

export const TRAVELER_DETAILS_QUERY = gql(/* GraphQL */ `
  query getSelectedTripOnTravelerDetailsPage($excludedExtraProductIds: [ID]) {
    countries {
      id
      name
      code
      phonePrefixes {
        id
        prefix
      }
    }
    cart {
      id
    }
    selectedTrip {
      id
      isVI
      selectionKey
      includedExtraProducts {
        id
        texts {
          name
          productSummaryAlternativeName
          readMoreText
          receiptText
          salesAbstract
        }
      }
      chargingCurrencyExchangeRate {
        currency {
          code
          exponent
          name
        }
        priceFormat {
          currencyExponent
          currencySymbol
          decimalSymbol
          groupingSeparator
          pattern
        }
        value
      }
      customer {
        email
        firstName
        lastName
        phone {
          countryCallingCode
          numberWithoutCountryCode
        }
      }
      availableAncillaryBundles {
        ...AncillaryBundlesOnTDPage
      }
      availableExtraProducts(excludedExtraProducts: $excludedExtraProductIds) {
        ...AvailableExtraProductsOnTDPage
      }
      bounds {
        boundId: id
        includedCheckedBaggage {
          ...IncludedCheckedBaggage
        }
        includedCabinBaggage {
          ...IncludedCabinBaggage
        }
        segments {
          ... on TripSegment {
            __typename
            arrivedAt
            aircraftType
            brandedFareInformation {
              name
            }
            includedCheckedBaggage {
              ...IncludedCheckedBaggage
            }
            includedCabinBaggage {
              ...IncludedCabinBaggage
            }
            cabinClassName
            departuredAt
            destination {
              code
              name
              cityCode
              cityName
              isAirport
            }
            duration
            equipmentCode
            flightNumber
            segmentId: id
            marketingCarrier {
              code
              name
            }
            numberOfTechnicalStops
            technicalStops {
              arrivalAt
              departureAt
              duration
              location {
                code
                name
                cityName
                cityCode
              }
            }
            operatingCarrier {
              code
              name
            }
            operatingInformation
            ticketingCarrier {
              code
              name
            }
            origin {
              code
              name
              cityCode
              cityName
              isAirport
            }
            segmentDetails {
              paxType
              numberOfSeatsLeft
            }
          }
          ... on EventSegment {
            __typename
            duration
            types
            segmentId: id
          }
        }
      }
      changes {
        ... on TripPriceChange {
          id
          priceNew {
            price {
              value
              currency {
                code
              }
            }
          }
          priceOld {
            price {
              value
              currency {
                code
              }
            }
          }
        }
        ... on TripScheduleChange {
          id
          arrivalTimeNew
          arrivalTimeOld
          departureTimeNew
          departureTimeOld
          segmentId
        }
      }
      frequentFlyerCardTypes {
        id
        name
      }
      paymentMethodPrices {
        name
        id
        price {
          value
        }
        type
      }
      travelers {
        age
        ageType
        birthDate
        contactDetails {
          email
          phone {
            countryCallingCode
            numberWithoutCountryCode
          }
        }
        firstName
        gender
        id
        lastName
        nationality
      }
      travelerPrices {
        id
        price {
          markup {
            value
          }
          price {
            value
            currency {
              code
            }
          }
          vat {
            value
          }
        }
        taxesAndFees {
          category
          taxes {
            code
            title
            amount {
              value
            }
          }
        }
        travelerId
      }
      travelerPricesWithoutPaymentDiscounts {
        price {
          markup {
            value
          }
          price {
            value
          }
          vat {
            value
          }
        }
        taxesAndFees {
          category
          taxes {
            code
            title
            amount {
              value
            }
          }
        }
        carrierPriceBreakdowns {
          carrier {
            name
            code
          }
          providerPrice {
            value
            currency {
              code
            }
          }
          vat {
            value
            currency {
              code
            }
          }
          taxesAndFees {
            category
            taxes {
              code
              title
              amount {
                value
                currency {
                  code
                }
              }
            }
          }
        }
        travelerId
      }
      tripCharacteristics
      checkedBaggageSummary {
        tripSummaries {
          travelerId
          baggageDetails {
            pieces
            weight
            weightUnit
          }
          baggageStatus
        }
      }
      cabinBaggageTripSummary {
        cabinBaggageTravelerDetails {
          cabinBaggageDetails {
            pieces
            weight
            weightUnit
          }
          cabinBaggageStatus
          travelerId
        }
      }
      personalItemTripSummary {
        personalItemTravelerDetails {
          personalItemStatus
          travelerId
          personalItemDetails {
            size3d {
              height
              length
              width
            }
          }
        }
      }
      includedCheckedBaggage {
        ...IncludedCheckedBaggage
      }
      includedCabinBaggage {
        ...IncludedCabinBaggage
      }
      type
      systems
      voucherAmount {
        value
      }
      shareableUrl
      mutuallyExclusiveProducts {
        productTypes
      }
    }
    travelerDetails {
      searchMetaData {
        selectionKey
        searchPath
      }
      shouldShowEmergencyContactInformation: showEmergencyContactInformation
      travelers {
        id
        shouldShowFrequentFlyerCard: showFrequentFlyerCard
        travelDocument {
          shouldShowTravelDocumentIssueDate: showTravelDocumentIssueDate
          shouldShowTravelDocumentIssueCity: showTravelDocumentIssueCity
          shouldShowTravelDocumentDateOfBirth: showTravelDocumentDateOfBirth
          shouldShowTravelDocumentNationality: showTravelDocumentNationality
          shouldShowTravelDocumentPassportNumber: showTravelDocumentPassportNumber
          shouldShowTravelDocumentCpfNumber: showTravelDocumentCpfNumber
          requireTravelDocumentExpiryDate
        }
      }
    }
    selfServiceRebooking {
      travelers {
        id
        ageType
        birthDate
        firstName
        lastName
        gender
      }
    }
  }
`);

export const SEATMAP_PRODUCT_QUERY = gql(/* GraphQL */ `
  query getSeatmapProduct($productId: ID!) {
    ancillaryItem(productId: $productId) {
      id
      name
      code
      selectedWithTrip
      productSpecificTexts {
        ... on InsuranceTexts {
          termsUrl
          title
          description
          inclusions {
            title
            description
          }
          disclaimer
          ipid {
            name
            url
          }
        }
        ... on SeatMapTexts {
          seatMapSegmentTexts {
            boundIndex
            segmentIndex
            text
          }
        }
      }
      sellSpecification {
        ... on SellSpecificationSeatingSeatMapNew {
          __typename
          sellSeatMapBounds {
            id
            sellSeatMapSegments {
              ... on SellSeatMapSegmentNew {
                __typename
                id
                travelerIds
                bestPriceCharacteristics {
                  characteristic
                  price
                }
                lowerDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                mainDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                upperDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
              }
              ... on SellSeatingPreference {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
              ... on SellSeatingBeside {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
            }
          }
        }
      }
      configuration {
        travelerDetails {
          persuasionEnabled
          showPricePerBound
          showPricePerTraveler
          highlightProduct
        }
        productPreSelection
      }
    }
  }
`);

export const ANCILLARY_ITEMS_QUERY = gql(/* GraphQL */ `
  query getAncillaryItems(
    $customerPhoneNumber: String
    $customerPhoneNumberPrefix: Int
    $productIds: [ID!]
  ) {
    ancillaryItems(
      customerPhoneNumber: $customerPhoneNumber
      customerPhoneNumberPrefix: $customerPhoneNumberPrefix
      productIds: $productIds
    ) {
      id
      name
      code
      selectedWithTrip
      productSpecificTexts {
        ... on InsuranceTexts {
          termsUrl
          title
          description
          inclusions {
            title
            description
          }
          disclaimer
          ipid {
            name
            url
          }
        }
        ... on SeatMapTexts {
          seatMapSegmentTexts {
            boundIndex
            segmentIndex
            text
          }
        }
        ... on ProductSegmentAvailabilityTexts {
          __typename
          productSegmentAvailabilityTexts {
            loungeInfo {
              closeAt
              loungeName
              openAt
              terminal
              amenities {
                code
                id
                name
              }
              conditions {
                code
                description
              }
            }
            arrivalAirport
            available
            boundIndex
            boundType
            departureAirport
            departureDateTime
            extSystem
            flightNumber
            segmentIndex
          }
        }
      }
      sellSpecification {
        ... on SellSpecificationNoneNew {
          __typename
          price {
            markup {
              value
              currency {
                code
              }
            }
            price {
              value
              currency {
                code
              }
            }
            vat {
              value
              currency {
                code
              }
            }
          }
        }
        ... on SellSpecificationBaggageNew {
          __typename
          sellPriceBaggage {
            travelerId
            hasMultipleWeightOptions
            options {
              numberOfUnits
              weight
              weightUnit
              preselected
              ancillaryCarrierPriceBreakdowns {
                carrier {
                  code
                  name
                }
                providerPrice {
                  value
                }
              }
              price {
                markup {
                  value
                }
                price {
                  value
                }
                vat {
                  value
                }
              }
            }
          }
        }
        ... on SellSpecificationSeatingSeatMapNew {
          __typename
          sellSeatMapBounds {
            id
            sellSeatMapSegments {
              ... on SellSeatMapSegmentNew {
                __typename
                id
                travelerIds
                bestPriceCharacteristics {
                  characteristic
                  price
                }
                lowerDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                mainDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
                upperDeck {
                  cabins {
                    rows {
                      characteristics
                      rowNumber
                      sellSeatMapSeats {
                        column
                        isBookable
                        price {
                          price {
                            value
                          }
                          markup {
                            value
                          }
                        }
                        row
                        seat
                        type
                        mainCharacteristic
                      }
                    }
                    sellSeatMapColumnDetails {
                      columnName
                      type
                    }
                  }
                }
              }
              ... on SellSeatingPreference {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
              ... on SellSeatingBeside {
                __typename
                price {
                  price {
                    value
                  }
                  markup {
                    value
                  }
                }
                id
              }
            }
          }
        }
        ... on SellSpecificationTravelerChoiceNew {
          __typename
          availableChoices {
            id
            name
            descriptions {
              text
              type
            }
            pricePerTravelers {
              travelerId
              price {
                price {
                  value
                }
              }
            }
          }
          preselectedChoice
          pricePerTravelers {
            travelerId
            price {
              markup {
                value
                currency {
                  code
                }
              }
              price {
                value
                currency {
                  code
                }
              }
              vat {
                value
                currency {
                  code
                }
              }
            }
          }
        }
        ... on SellSpecificationTravelerNew {
          __typename
          sellPriceTravelers {
            travelerId
            price {
              markup {
                value
                currency {
                  code
                }
              }
              price {
                value
                currency {
                  code
                }
              }
              vat {
                value
                currency {
                  code
                }
              }
            }
          }
        }
        ... on SellCabinBaggageNew {
          weight
          weightUnit
          pieces
          size3d {
            height
            length
            width
          }
          sellPriceTravelers {
            travelerId
            price {
              price {
                value
                currency {
                  code
                }
              }
              markup {
                value
                currency {
                  code
                }
              }
              vat {
                currency {
                  code
                }
                value
              }
            }
          }
        }
      }
      configuration {
        travelerDetails {
          persuasionEnabled
          showPricePerBound
          showPricePerTraveler
          highlightProduct
        }
        productPreSelection
      }
    }
  }
`);

export const VALIDATE_EMAIL = gql(/* GraphQL */ `
  query ValidateEmail($email: String!) {
    validateEmail(email: $email) {
      valid
      message
    }
  }
`);

export const VALIDATE_PHONE_NUMBER = gql(/* GraphQL */ `
  query ValidatePhoneNumber($phoneNumberAfterPrefix: String!, $prefixId: ID!) {
    validatePhoneNumber(phoneNumberAfterPrefix: $phoneNumberAfterPrefix, prefixId: $prefixId) {
      valid
      message
    }
  }
`);

export const SELF_TRANSFER_FAQ = gql(/* GraphQL */ `
  query getSelfTransferFaqSections {
    faq(faqType: SELF_TRANSFER) {
      sections {
        questionAndAnswers {
          answer
          question
          slug
        }
        slug
        title
      }
    }
  }
`);
